<template>
  <div class="d-flex align-start" style="gap: 8px;">
    <v-autocomplete
      v-bind:value="value.license_plate_country_code"
      v-on:input="
        $emit('input', { ...value, license_plate_country_code: $event })
      "
      :label="$t('label.country')"
      style="max-width: 90px; min-width: 70px; flex-shrink: 0;"
      :items="countries"
      :loading="validatingLicensePlateLoader"
      :rules="required ? [v => !!v || $t('rules.field_required')] : []"
      validate-on-change
      @change="$nextTick(validateLicensePlate)"
    >
      <template v-if="required" #label>
        <span class="red--text"><strong>* </strong></span>
        {{ $t("label.country") }}
      </template>
    </v-autocomplete>
    <v-text-field
      v-bind:value="value.license_plate"
      v-on:input="$emit('input', { ...value, license_plate: $event })"
      :label="label ? label : $t('label.license_plate')"
      :rules="required ? [v => !!v || $t('rules.field_required')] : []"
      validate-on-blur
      :required="required"
      :loading="validatingLicensePlateLoader"
      @blur="validateLicensePlate()"
      :success-messages="validatingLicensePlateSuccessMessage"
      :error-messages="licenseErrorMessage"
      :messages="validatingLicensePlateLoadingMessage"
      :append-icon="validatingLicensePlateLoadingIcon"
      class="flex-grow-1"
    >
      <template v-if="required" #label>
        <span class="red--text"><strong>* </strong></span>
        {{ label ? label : $t("label.license_plate") }}
      </template>
    </v-text-field>
  </div>
</template>

<script>
export default {
  name: "LicensePlateField",
  model: {
    prop: "value",
    event: "input"
  },
  props: {
    value: {
      type: Object,
      default: () => ({ license_plate: "", license_plate_country_code: "NLD" })
    },
    originalValue: {
      type: Object,
      default: () => ({ license_plate: "", license_plate_country_code: "NLD" }),
      required: false
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    submitError: {
      type: Object,
      default: null
    },
    excludeOwnCardSubscriptions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      validatingLicensePlateLoader: false,
      validatingLicensePlateSuccessMessage: "",
      validatingLicensePlateErrorMessage: "",
      validatingLicensePlateLoadingMessage: "",
      validatingLicensePlateLoadingIcon: null,
      licensePlateValid: false,
      countries: [
        {
          text: "🇳🇱 NL",
          value: "NLD"
        },
        {
          text: "🇧🇪 BE",
          value: "BEL"
        },
        {
          text: "🇩🇪 DE",
          value: "DEU"
        },
        {
          text: "🇫🇷 FR",
          value: "FRA"
        }
      ]
    };
  },
  computed: {
    submitErrorMap() {
      return {
        exceeded_license_plate_changes: this.$t(
          "error.exceeded_license_plate_changes"
        ),
        same_license_plate: this.$t("error.same_license_plate")
      };
    },
    licensePlateChanged() {
      return (
        this.value.license_plate !== this.originalValue.license_plate ||
        this.value.license_plate_country_code !==
          this.originalValue.license_plate_country_code
      );
    },
    licenseErrorMessage() {
      if (this.validatingLicensePlateErrorMessage) {
        return this.validatingLicensePlateErrorMessage;
      }
      if (this.submitError && this.submitError.error) {
        return (
          this.submitErrorMap[this.submitError.error] ||
          this.$t("error.something_went_wrong")
        );
      }
      return "";
    }
  },
  watch: {
    "value.license_plate"(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.reset();
      }
    }
  },
  created() {
    if (!this.value.license_plate_country_code) {
      this.$emit("input", {
        ...this.value,
        license_plate_country_code: this.countries[0].value
      });
    }
  },
  methods: {
    reset() {
      this.licensePlateValid = false;
      this.validatingLicensePlateLoader = false;
      this.validatingLicensePlateSuccessMessage = "";
      this.validatingLicensePlateErrorMessage = "";
      this.validatingLicensePlateLoadingIcon = null;
      this.validatingLicensePlateLoadingMessage = "";
      this.$emit("valid", false);
      this.$emit("clear-error");
    },
    validateLicensePlate() {
      this.validatingLicensePlateErrorMessage = "";
      this.validatingLicensePlateSuccessMessage = "";
      this.validatingLicensePlateLoadingIcon = null;
      this.licensePlateValid = false;

      const value = this.value;

      if (
        !value ||
        !value.license_plate ||
        !value.license_plate_country_code ||
        value.license_plate.length === 0
      )
        return;
      if (!this.licensePlateChanged) return;

      this.validatingLicensePlateLoader = true;
      this.$emit("validating", true);
      this.validatingLicensePlateLoadingMessage = this.$t(
        "message.controlling_license_plate"
      );

      this.$store
        .dispatch("user/validateLicensePlate", {
          license_plate: value.license_plate,
          license_plate_country_code: value.license_plate_country_code,
          exclude_own_card_subscriptions: this.excludeOwnCardSubscriptions
        })
        .then(response => {
          const used = response.data.used;
          if (used) {
            this.validatingLicensePlateLoadingIcon = "mdi-cancel";
            this.validatingLicensePlateErrorMessage = this.$t(
              "error.license_plate_is_used"
            );
            this.licensePlateValid = false;
            this.$emit("valid", false);
          } else {
            this.validatingLicensePlateLoadingIcon = "mdi-check";
            this.validatingLicensePlateSuccessMessage = this.$t(
              "success.license_plate_valid"
            );
            this.licensePlateValid = true;
            this.$emit("valid", true);
          }
        })
        .catch(error => {
          const response = error.response || {};
          const status = response.status;
          const data = response.data || {};

          if (data.error === "invalid_license_plate") {
            this.validatingLicensePlateLoadingIcon = "mdi-cancel";
            this.validatingLicensePlateErrorMessage = this.$t(
              "error.invalid_license_plate"
            );
          } else if (status === 429) {
            this.validatingLicensePlateErrorMessage = this.$t(
              "error.too_many_requests"
            );
          } else {
            this.validatingLicensePlateErrorMessage = this.$t(
              "error.something_went_wrong"
            );
          }

          this.licensePlateValid = false;
          this.$emit("valid", false);
        })
        .finally(() => {
          this.validatingLicensePlateLoader = false;
          this.validatingLicensePlateLoadingMessage = "";
          this.$emit("validating", false);
        });
    }
  }
};
</script>
